import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'

import NAVAssignmentDropdown from 'components/brokers/needs_attention/NAVAssignmentDropdown'
import {
  filterByLabelAndExactValueCallback,
  setQueryDataCallback,
} from 'components/brokers/needs_attention/utils'
import { type Broker } from 'components/brokers/types'
import BrokerContext from 'components/context/BrokerContext'
import { useGetBrokers } from 'components/hooks/needs_attention/useGetBrokers'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import { fetchWithHeaders } from 'utils'
import { getGetAdminEmployersQueryKey, usePatchAdminEmployersEmployerId } from 'utils/web_api'
import { type AdminEmployer, type BrokersBroker, type GetAdminEmployersParams } from 'utils/web_api.schemas'

type AssignBrokersToEmployerProps = {
  id: string
  assignedBrokers: BrokersBroker[]
  params?: GetAdminEmployersParams
}

const AssignBrokersToEmployer = ({
  id,
  assignedBrokers,
  params,
}: AssignBrokersToEmployerProps) => {
  const queryClient = useQueryClient()
  const { currentBroker } = useContext(BrokerContext)
  const [query, setQuery] = useState('')

  const { data } = useGetBrokers()
  const brokers = data?.data || []

  const pinnedBrokerOptions: ComboboxFilterOption<string>[] =
    currentBroker && currentBroker.id
      ? [{ label: currentBroker.broker_name || currentBroker.id, value: currentBroker.id }]
      : []

  const brokerOptions: ComboboxFilterOption<string>[] =
    brokers
      .map((broker: Broker) => ({
        label: broker.broker_name,
        value: broker.id,
      }))
      .filter((broker) => broker.value !== currentBroker?.id)

  const assignBrokersToEmployerMutation = usePatchAdminEmployersEmployerId({
    mutation: {
      onSuccess: (data) => {
        // Update cached data with new employer items that include updated broker assignments.
        if (params) {
          queryClient.setQueryData(
            getGetAdminEmployersQueryKey(params),
            setQueryDataCallback([data])
          )
        }
      },
    },
  })

  const assignedBrokerIds = assignedBrokers.map((broker) => broker.id)

  const handleSelectBroker = async (brokerId: string) => {
    // Note: usePatchAdminEmployersEmployerId is for review_status updates.
    // For broker assignments, we use the bulk endpoint directly.
    try {
      const response = await fetchWithHeaders('/api/web/admin/employers', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employer_ids: [id],
          ...(assignedBrokerIds.includes(brokerId)
            ? { broker_ids_to_remove: [brokerId] }
            : { broker_ids_to_add: [brokerId] }),
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to update employer assignments')
      }

      const data = (await response.json()) as AdminEmployer[]

      if (params) {
        queryClient.setQueryData(
          getGetAdminEmployersQueryKey(params),
          setQueryDataCallback(data)
        )
      }
    } catch (error) {
      console.error('Error updating employer assignments:', error)
    }
  }

  const filteredPinnedBrokerOptions = pinnedBrokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )
  const filteredBrokerOptions = brokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ComboboxMultiSelectFilter<string>
        selectedValues={assignedBrokerIds}
        options={[
          { groupLabel: 'Pinned', options: filteredPinnedBrokerOptions },
          { groupLabel: 'Brokers', options: filteredBrokerOptions },
        ]}
        onSelectValue={handleSelectBroker}
        placeholder='Search brokers to assign...'
        onValueChange={setQuery}
      >
        <NAVAssignmentDropdown
          noSelectionText='Assignee'
          pluralIdentifier='assignees'
          selectedValues={assignedBrokerIds}
          options={[...pinnedBrokerOptions, ...brokerOptions]}
          isPending={assignBrokersToEmployerMutation.isPending}
          type='assignee'
        />
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default AssignBrokersToEmployer
